import { KeyboardEvent, useRef } from "react";
import { t } from "../../i18n";
import { SeoLocale, SeoMetadata } from "../../http/protocol";
import { hasOgImageError, hasOwnValue } from "./seoEditorUtils";
import { SeoMetadataByLocaleType } from "./seoTypes";

interface SeoLocaleTabsProps {
  /** Already in display order. */
  locales: SeoLocale[];
  values: SeoMetadataByLocaleType;
  activeCode: string;
  panelId: string;
  onSelect: (code: string) => void;
  testIdPrefix: string;
}

interface SeoLocaleTabProps {
  locale: SeoLocale;
  value: SeoMetadata;
  active: boolean;
  panelId: string;
  onSelect: (code: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  testIdPrefix: string;
}

function markerClassName(ownValue: boolean, error: boolean): string {
  if (error) {
    return "seo-locale-tabs__marker text-danger";
  }
  return ownValue ? "seo-locale-tabs__marker text-success" : "seo-locale-tabs__marker text-muted";
}

function SeoLocaleTab({ locale, value, active, panelId, onSelect, onKeyDown, testIdPrefix }: SeoLocaleTabProps): JSX.Element {
  const code = locale.code ?? "";
  const tabTestId = `${testIdPrefix}-tab-${code}`;
  const ownValue = hasOwnValue(value);
  const ownValueLabel = ownValue
    ? t("admin.seo.tab.own-value", undefined, "Has its own SEO values")
    : t("admin.seo.tab.uses-fallback", undefined, "Uses fallback values");
  const errorLabel = t("admin.seo.tab.invalid-og-image-url", undefined, "Has an invalid social share image URL");
  const ogImageError = hasOgImageError(value);
  // The tab's accessible name is read in DOM order, so the status labels follow the locale code
  // and the tab is announced as "en Default, Has its own SEO values".
  return (
    <li className="nav-item">
      <button
        type="button"
        id={tabTestId}
        className={active ? "nav-link active" : "nav-link"}
        role="tab"
        aria-selected={active}
        aria-controls={panelId}
        tabIndex={active ? 0 : -1}
        data-testid={tabTestId}
        onClick={() => onSelect(code)}
        onKeyDown={onKeyDown}
      >
        <span
          className={markerClassName(ownValue, ogImageError)}
          title={ogImageError ? `${ownValueLabel}, ${errorLabel}` : ownValueLabel}
          aria-hidden="true"
          data-testid={`${tabTestId}-marker`}
          data-own-value={ownValue}
          data-error={ogImageError}
        >
          {ownValue ? "●" : "○"}
        </span>
        {code}
        {locale.defaultLocale && <span className="badge badge-secondary ml-2">{t("admin.seo.default-locale", undefined, "Default")}</span>}
        <span
          className="sr-only"
          data-testid={`${tabTestId}-marker-label`}
        >
          {`, ${ownValueLabel}`}
        </span>
        {ogImageError && (
          <span
            className="sr-only"
            data-testid={`${tabTestId}-error-label`}
          >
            {`, ${errorLabel}`}
          </span>
        )}
      </button>
    </li>
  );
}

/** The locale tab row: one tab per locale, arrow/Home/End keys move between tabs. */
export function SeoLocaleTabs({ locales, values, activeCode, panelId, onSelect, testIdPrefix }: SeoLocaleTabsProps): JSX.Element {
  const tablistRef = useRef<HTMLUListElement>(null);

  function nextTabIndex(key: string, current: number): number | undefined {
    const last = locales.length - 1;
    switch (key) {
      case "ArrowLeft":
        return current === 0 ? last : current - 1;
      case "ArrowRight":
        return current === last ? 0 : current + 1;
      case "Home":
        return 0;
      case "End":
        return last;
      default:
        return undefined;
    }
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    const current = locales.findIndex((locale) => locale.code === activeCode);
    const next = nextTabIndex(event.key, current);
    if (next === undefined) {
      return;
    }
    event.preventDefault();
    onSelect(locales[next].code ?? "");
    tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  }

  return (
    <ul
      ref={tablistRef}
      className="nav nav-tabs seo-locale-tabs mb-3"
      role="tablist"
      data-testid={`${testIdPrefix}-tabs`}
    >
      {locales.map((locale) => {
        const code = locale.code ?? "";
        return (
          <SeoLocaleTab
            key={code}
            locale={locale}
            value={values[code] ?? {}}
            active={code === activeCode}
            panelId={panelId}
            onSelect={onSelect}
            onKeyDown={handleTabKeyDown}
            testIdPrefix={testIdPrefix}
          />
        );
      })}
    </ul>
  );
}
